import { WebChartThemeHelpers } from "../../helpers/webchart-theme-helpers";
import { TimeFrameToChartTimeFrameMapper } from "../../js-api-mappers/timeframe/time-frame-to-chart-time-frame.mapper";
import { WebChartDataService } from "../../services/web-chart-data.service";
import { WebChartsMarketingContainer } from "../../web-charts-marketing.container";
import { IChartContainer } from "../base/chart-container.interface";
import { Timeframe } from "../enum/timeframe.enum";

declare const window: any;

export enum ChartMode {
    Standard = 'Standard',
    Summary = 'Summary',
    Simple = 'Simple',
    Preview = 'Preview'
}

export enum ChartTheme {
    Dark = 'Dark',
    Black = 'Black',
    LightBlue = 'LightBlue',
    Light = 'Light',
    UpdatedWebDark = 'UpdatedWebDark'
}

export class MarketingWebChartWindowWrapper {

    private chartReady = false;

    private chartReadyWatchers: Array<Function> = [];

    private chartContainer: IChartContainer;
    constructor(base: IChartContainer) {
        this.chartContainer = base;
        this.onSetSymbolAndTimeframeListener();
    }

    public onChartReady() {
        this.chartReady = true;

        if (this.chartReadyWatchers.length) {
            for (const fx of this.chartReadyWatchers) {
                fx();
            }
        }

        this.chartReadyWatchers = [];
    }

    private onSetSymbolAndTimeframeListener() {
        const setSymbol = (symbol: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setSymbol(symbol);
                });
            } else {
                this.setSymbol(symbol);
            }
        };

        const setMainTimeframe = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setMainTimeframe(timeframeString);
                });
            } else {
                this.setMainTimeframe(timeframeString);
            }
        };

        const setPipTimeframe = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setPipTimeframe(timeframeString);
                });
            } else {
                this.setPipTimeframe(timeframeString);
            }
        };

        const setTimeframeStyle = (timeframeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTimeframeStyle(timeframeString);
                });
            } else {
                this.setTimeframeStyle(timeframeString);
            }
        };

        const setTheme = (themeString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setTheme(themeString);
                });
            } else {
                this.setTheme(themeString);
            }
        };

        const setMainChartLineAndFillColor = (lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
                });
            } else {
                this.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
            }
        };

        const setUpColor = (colorString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setUpColor(colorString);
                });
            } else {
                this.setUpColor(colorString);
            }
        };

        const setDownColor = (colorString: string) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setDownColor(colorString);
                });
            } else {
                this.setDownColor(colorString);
            }
        };

        const setPreviewSizePercent = (percentage: number) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setPreviewSizePercent(percentage);
                });
            } else {
                this.setPreviewSizePercent(percentage);
            }
        };

        const setUseLoginPopup = (on: boolean) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setUseLoginPopup(on);
                });
            } else {
                this.setUseLoginPopup(on);
            }
        };

        const setDebugTrace = (on: boolean) => {
            if (!this.chartReady) {
                this.chartReadyWatchers.push(() => {
                    this.setDebugTrace(on);
                });
            } else {
                this.setDebugTrace(on);
            }
        };


        const afterResize = () => {
            this.afterResize();
        };

        if (!window.chart) {
            window.chart = {};
            window.chart.setSymbol = setSymbol;
            window.chart.setTimeframe = setMainTimeframe;
            window.chart.setMainTimeframe = setMainTimeframe;
            window.chart.setPipTimeframe = setPipTimeframe;
            window.chart.setTimeframeStyle = setTimeframeStyle;
            window.chart.setTheme = setTheme;
            window.chart.setMainChartLineAndFillColor = setMainChartLineAndFillColor;
            window.chart.setUpColor = setUpColor;
            window.chart.setDownColor = setDownColor;
            window.chart.setPreviewSizePercent = setPreviewSizePercent;
            window.chart.setUseLoginPopup = setUseLoginPopup;
            window.chart.setDebugTrace = setDebugTrace;
            window.chart.afterResize = afterResize;
        }

        let existingChartMap = new Map();
        if (window.chartsMap) {
            existingChartMap = window.chartsMap;
        }

        if (!existingChartMap.has(this.chartContainer.id)) {
            existingChartMap.set(this.chartContainer.id, {
                id: this.chartContainer.id,
                setSymbol: setSymbol,
                setMainTimeframe: setMainTimeframe,
                setPipTimeframe: setPipTimeframe,
                setTimeframeStyle: setTimeframeStyle,
                setTheme: setTheme,
                setMainChartLineAndFillColor: setMainChartLineAndFillColor,
                setUpColor: setUpColor,
                setDownColor: setDownColor,
                setPreviewSizePercent: setPreviewSizePercent,
                setUseLoginPopup: setUseLoginPopup,
                setDebugTrace: setDebugTrace,
                afterResize: afterResize
            });
        }

        if (existingChartMap.size > 0) {
            const sortedChartArray = Array.from(existingChartMap);
            const chartsById: Array<any> = [];
            const charts: Array<any> = [];

            for (let i = 0; i < sortedChartArray.length; i++) {
                const id = sortedChartArray[i][0];
                const chart = sortedChartArray[i][1];
                chartsById[id] = chart;
                charts[i] = chart;
            }

            window.chartsById = chartsById;
            window.charts = charts;
        }

        window.chartsMap = existingChartMap;
    }

    private setSymbol(symbol: string) {
        if (!symbol) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const symbolUpperCase = symbol.toUpperCase();
        chartContainer.chartService.requestNewSymbol(symbolUpperCase);
        chartContainer.pnpContainer?.chartService.requestNewSymbol(symbolUpperCase);
    }

    private setMainTimeframe(timeframeString: string) {
        if (!timeframeString) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframeStringUpperCase = timeframeString.toUpperCase();
        const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeStringUpperCase);
        chartContainer.chartService.requestNewTimeframe(timeframe);
    }

    private setPipTimeframe(timeframeString: string) {
        if (!timeframeString) {
            return;
        }

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const timeframeStringUpperCase = timeframeString.toUpperCase();
        const timeframe = TimeFrameToChartTimeFrameMapper.map(timeframeStringUpperCase);
        chartContainer.pnpContainer?.chartService.requestNewTimeframe(timeframe);
    }

    private setTimeframeStyle(timeframeString: string) {
        const timeFrameStyle = timeframeString?.trim()?.toLowerCase() ?? '';
        if (!timeFrameStyle) {
            return;
        }

        let mainTimeframe = Timeframe.Day;
        let pipTimeframe = Timeframe.Day;
        if (timeFrameStyle == 'daytrading') {
            mainTimeframe = Timeframe.FiveMinute;
            pipTimeframe = Timeframe.Day;
        } else if (timeFrameStyle == 'swingtrading') {
            mainTimeframe = Timeframe.Day;
            pipTimeframe = Timeframe.Week;
        } else if (timeFrameStyle == 'investing') {
            mainTimeframe = Timeframe.Week;
            pipTimeframe = Timeframe.Month;
        } else {
            return;
        }
        
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        chartContainer.chartService.requestNewTimeframe(mainTimeframe);
        chartContainer.pnpContainer?.chartService.requestNewTimeframe(pipTimeframe);
    }

    private setTheme(themeString: string) {
        if (!themeString) {
            return;
        }

        const theme = WebChartThemeHelpers.getTheme(themeString.toLowerCase());

        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        const pnpChartService = chartContainer.pnpContainer?.chartService as WebChartDataService;

        chartService.setChartStyleData(theme);
        chartService.onSubscribeChartStyleData();

        pnpChartService?.setChartStyleData(theme);
        pnpChartService?.onSubscribeChartStyleData();
    }

    private setUpColor(colorString: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setSelectedColor(colorString, true);
        chartService.onSubscribeInitChartData();
    }

    private setDownColor(colorString: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setSelectedColor(colorString, false);
        chartService.onSubscribeInitChartData();
    }

    private setMainChartLineAndFillColor(lineHexColor: string, areaFillHexColor: string, gradientHexColor: string) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setMainChartLineAndFillColor(lineHexColor, areaFillHexColor, gradientHexColor);
        chartService.onSubscribeInitChartData();
    }

    private setPreviewSizePercent(percentage: number) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setChartPreviewSymbolSizePercent(percentage);
        chartService.onSubscribeInitChartData();
    }

    private setUseLoginPopup(on: boolean) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setUseLoginPopup(on);
        chartService.logout();

        chartContainer.handleLogin();
    }

    private setDebugTrace(on: boolean) {
        const chartContainer = this.chartContainer as WebChartsMarketingContainer;
        const chartService = chartContainer.chartService as WebChartDataService;
        chartService.setDebugTrace(on);
        chartService.onSubscribeInitChartData();
    }

    private afterResizeTimer: any;
    private afterResize() {
        clearTimeout(this.afterResizeTimer);
        this.afterResizeTimer = setTimeout(() => {
            const chartContainer = this.chartContainer as WebChartsMarketingContainer;
            chartContainer?.tiChartSeries?.afterResize();
            chartContainer?.pnpContainer?.tiChartSeries?.afterResize();
            chartContainer?.pnpContainer?.setPnPSize();
        }, 200);
    }
}